// Recap owner display helpers shared by recaps list cards and tracked meeting details.

export const getOwnerFromEvent = (event, ownerUserUuid) => {
  if (!ownerUserUuid) return null

  const ownerAttendee = event.attendees?.find(({ uuid }) => uuid === ownerUserUuid)
  if (ownerAttendee) {
    return {
      name: ownerAttendee.name ?? null,
      email: ownerAttendee.email ?? null,
      avatarUrl: ownerAttendee.avatar_url ?? null,
    }
  }

  return null
}

export const getOwnerUserUuidsForLookup = (meetings, currentUserUuid) => (
  [...new Set(
    meetings
      .map(({ ownerUserUuid }) => ownerUserUuid)
      .filter((uuid) => uuid && uuid !== currentUserUuid),
  )]
)

export const buildOwnersByUserUuid = (memberships) => (
  memberships.reduce((acc, { user_uuid: userUuid, name, email, avatar_url: avatarUrl }) => {
    if (userUuid) {
      acc[userUuid] = {
        name: name ?? null,
        email: email ?? null,
        avatarUrl: avatarUrl ?? null,
        avatarLetter: name?.[0]?.toUpperCase() ?? null,
      }
    }

    return acc
  }, {})
)

const getFallbackAvatarLetter = (displayName, email) => {
  if (email && /^\d/.test(email)) {
    return null
  }

  return displayName?.[0]?.toUpperCase() ?? null
}

const buildOwnerShape = (uuid, {
  name = null,
  email = null,
  avatarUrl = null,
  avatarLetter = null,
  displayName = null,
  isCurrentUserOwner = false,
} = {}) => ({
  uuid,
  name,
  email,
  avatarUrl,
  avatarLetter,
  displayName,
  isCurrentUserOwner,
})

export const resolveOwner = ({
  event,
  ownersByUuid,
  skip = false,
  currentUserUuid = null,
}) => {
  const { ownerUserUuid } = event
  const isCurrentUserOwner = Boolean(currentUserUuid && ownerUserUuid === currentUserUuid)

  if (skip || !ownerUserUuid) return null

  if (isCurrentUserOwner) {
    return buildOwnerShape(ownerUserUuid, { isCurrentUserOwner: true })
  }

  const eventOwner = getOwnerFromEvent(event, ownerUserUuid)
  const batchOwner = ownersByUuid[ownerUserUuid]

  const name = batchOwner?.name ?? eventOwner?.name ?? null
  const email = batchOwner?.email ?? eventOwner?.email ?? null
  const avatarUrl = batchOwner?.avatarUrl ?? eventOwner?.avatarUrl ?? null
  const displayName = name || email

  if (!displayName) {
    return buildOwnerShape(ownerUserUuid)
  }

  const avatarLetter = name
    ? name[0]?.toUpperCase() ?? null
    : getFallbackAvatarLetter(displayName, email)

  return buildOwnerShape(ownerUserUuid, {
    name,
    email,
    avatarUrl,
    avatarLetter,
    displayName,
  })
}
